import isEqual from 'react-fast-compare';

import {
  StructuredTableColumn,
  UnknownStructuredTable,
} from 'notebook/structured-tables/StructuredTableConstants';
import {hasNLO, isStructuredTable} from 'notebook/TypePredicates';
import {NoteLinkedObjectType, NotePart} from 'notebook/util/NoteUtilConstants';
import {omit} from 'util/omitKey';

interface RuntimeProperty<T> {
  fields: Array<keyof T>;
  children: {
    [P in keyof T]?: T[P] extends Array<infer R> ? {fields: Array<keyof R>} : never;
  };
}

const RUNTIME_PROPERTIES = {
  [NoteLinkedObjectType.STRUCTURED_TABLE]: {
    fields: ['numApiRowsToAdd'],
    children: {columns: {fields: ['defaultValue']}},
  } satisfies RuntimeProperty<UnknownStructuredTable>,
} as const;

function removeRuntimeProperties<NP extends NotePart>(notePart: NP): NP {
  if (hasNLO(notePart) && isStructuredTable(notePart.item)) {
    let cleanedColumns: Array<Partial<StructuredTableColumn>> = notePart.item.columns;

    // Template tables with one row automatically get their first row values set as the default values for the
    // columns in fromTable. These need to be omitted from the equality check
    if (notePart.item.isConfig && notePart.item.rows.length === 1) {
      cleanedColumns = notePart.item.columns.map((column) =>
        omit<StructuredTableColumn, Array<keyof StructuredTableColumn>>(
          column,
          ...RUNTIME_PROPERTIES[NoteLinkedObjectType.STRUCTURED_TABLE].children.columns.fields
        )
      );
    }

    const cleanedItem = omit(
      {...notePart.item, columns: cleanedColumns},
      ...RUNTIME_PROPERTIES[NoteLinkedObjectType.STRUCTURED_TABLE].fields
    );
    cleanedItem.filterInfo ??= undefined;

    return {
      ...notePart,
      item: cleanedItem,
    };
  }

  return notePart;
}

/**
 * Returns if two note parts are equal. Omits any NotePart properties that don't affect equality when
 * comparing.
 */
export default function areNotePartsEqual<NPA extends NotePart, NPB extends NotePart>(
  notePartA: NPA,
  notePartB: NPB
): boolean {
  const cleanedNotePartA = removeRuntimeProperties(notePartA);
  const cleanedNotePartB = removeRuntimeProperties(notePartB);
  return isEqual(cleanedNotePartA, cleanedNotePartB);
}
